import { defineStore } from "pinia";
import { computed, ref } from "vue";

import {
  beginGoogleAccountLogin as beginGoogleAccountLoginRequest,
  beginMicrosoftAccountLogin as beginMicrosoftAccountLoginRequest,
  completeLoginTotp,
  getCurrentUser,
  getLoginCapabilities,
  login as loginRequest,
  logout as logoutRequest,
  register as registerRequest,
  requestPasswordReset as passwordResetRequest,
  resetPassword as passwordReset,
} from "@/api/auth";
import { advanceAuthenticationBoundary } from "@/api/http";
import { resourceCache } from "@/cache/resourceCache";
import { ApiError, type AsyncStatus } from "@/types/api";
import type { TwoFactorChallengeResponse } from "@/types/accountSecurity";
import { isTwoFactorChallenge } from "@/types/accountSecurity";
import type { CandidateProfile } from "@/types/profile";
import type {
  LoginCredentials,
  LoginState,
  RegistrationDetails,
  User,
  UserCapability,
} from "@/types/user";

export const useUserStore = defineStore("user", () => {
  const user = ref<User | null>(null);
  const profile = ref<CandidateProfile | null>(null);
  const loginState = ref<LoginState>("unknown");
  const authStatus = ref<AsyncStatus>("idle");
  const profileStatus = ref<AsyncStatus>("idle");
  const sessionError = ref<ApiError | null>(null);
  const authError = ref<ApiError | null>(null);
  let sessionRequest: Promise<User | null> | null = null;
  let unauthorizedConfirmationRequest: Promise<void> | null = null;
  let sessionGeneration = 0;
  let authOperationGeneration = 0;

  function advanceSessionGeneration(): number {
    sessionGeneration += 1;
    sessionRequest = null;
    return sessionGeneration;
  }

  function beginAuthOperation(): number {
    advanceAuthenticationBoundary();
    advanceSessionGeneration();
    authOperationGeneration += 1;
    return authOperationGeneration;
  }

  function ownsSessionGeneration(requestGeneration: number): boolean {
    return sessionGeneration === requestGeneration;
  }

  function ownsAuthOperation(requestGeneration: number): boolean {
    return authOperationGeneration === requestGeneration;
  }

  const isAuthenticated = computed(() => loginState.value === "authenticated");
  const displayName = computed(() => {
    const candidate = user.value?.username.trim() || user.value?.email.trim() || "Account";
    return candidate.includes("@") ? candidate.split("@", 1)[0] || "Account" : candidate;
  });
  const initial = computed(() => displayName.value.slice(0, 1).toUpperCase() || "A");

  function hasCapability(capability: UserCapability): boolean {
    return user.value?.capabilities.includes(capability) === true;
  }

  function beginMicrosoftAccountLogin(): void {
    beginAuthOperation();
    authError.value = null;
    beginMicrosoftAccountLoginRequest();
  }

  function beginGoogleAccountLogin(): void {
    beginAuthOperation();
    authError.value = null;
    beginGoogleAccountLoginRequest();
  }

  async function loadLoginCapabilities(): Promise<boolean> {
    try {
      return (await getLoginCapabilities()).google_login_enabled === true;
    } catch {
      return false;
    }
  }

  function normalizeAuthFailure(caught: unknown): ApiError {
    return caught instanceof ApiError
      ? caught
      : new ApiError("Authentication could not be completed.");
  }

  function failAuthentication(caught: unknown): ApiError {
    const normalized = normalizeAuthFailure(caught);
    authStatus.value = "error";
    authError.value = normalized;
    return normalized;
  }

  function acceptSession(authenticatedUser: User): User {
    advanceSessionGeneration();
    if (user.value?.id !== authenticatedUser.id) resourceCache.clear();
    user.value = authenticatedUser;
    loginState.value = "authenticated";
    sessionError.value = null;
    return authenticatedUser;
  }

  function acceptAuthentication(authenticatedUser: User): User {
    acceptSession(authenticatedUser);
    authStatus.value = "success";
    authError.value = null;
    return authenticatedUser;
  }

  function markUnauthenticated(): void {
    advanceSessionGeneration();
    resourceCache.clear();
    user.value = null;
    profile.value = null;
    profileStatus.value = "idle";
    loginState.value = "unauthenticated";
    sessionError.value = null;
  }

  function failSession(caught: unknown): ApiError {
    const normalized = normalizeAuthFailure(caught);
    advanceSessionGeneration();
    resourceCache.clear();
    user.value = null;
    profile.value = null;
    profileStatus.value = "idle";
    loginState.value = "error";
    sessionError.value = normalized;
    return normalized;
  }

  function ensureSession(): Promise<User | null> {
    if (user.value && loginState.value === "authenticated") {
      return Promise.resolve(user.value);
    }
    if (sessionRequest) return sessionRequest;
    const requestGeneration = advanceSessionGeneration();
    loginState.value = "loading";
    sessionError.value = null;
    const request = getCurrentUser()
      .then((authenticatedUser) => {
        if (!ownsSessionGeneration(requestGeneration)) return user.value;
        return acceptSession(authenticatedUser);
      })
      .catch((caught: unknown) => {
        if (!ownsSessionGeneration(requestGeneration)) return user.value;
        const normalized = caught instanceof ApiError
          ? caught
          : new ApiError("Authentication could not be checked.");
        if (normalized.status === 401) {
          markUnauthenticated();
          return null;
        }
        throw failSession(normalized);
      });
    sessionRequest = request;
    void request.finally(() => {
      if (sessionRequest === request) sessionRequest = null;
    }).catch(() => undefined);
    return request;
  }

  async function refreshSession(): Promise<User | null> {
    const requestGeneration = advanceSessionGeneration();
    sessionError.value = null;
    try {
      const authenticatedUser = await getCurrentUser();
      if (!ownsSessionGeneration(requestGeneration)) return user.value;
      return acceptSession(authenticatedUser);
    } catch (caught: unknown) {
      if (!ownsSessionGeneration(requestGeneration)) return user.value;
      const normalized = caught instanceof ApiError
        ? caught
        : new ApiError("Authentication could not be checked.");
      if (normalized.status === 401) {
        markUnauthenticated();
        return null;
      }
      throw failSession(normalized);
    }
  }

  function confirmUnauthorized(): Promise<void> {
    if (loginState.value !== "authenticated" || !user.value) {
      return Promise.resolve();
    }
    if (unauthorizedConfirmationRequest) return unauthorizedConfirmationRequest;
    const requestGeneration = sessionGeneration;
    const request = getCurrentUser()
      .then((authenticatedUser) => {
        if (!ownsSessionGeneration(requestGeneration)) return;
        acceptSession(authenticatedUser);
      })
      .catch((caught: unknown) => {
        if (!ownsSessionGeneration(requestGeneration)) return;
        const normalized = caught instanceof ApiError
          ? caught
          : new ApiError("Authentication could not be checked.");
        if (normalized.status === 401) markUnauthenticated();
      });
    unauthorizedConfirmationRequest = request;
    void request.finally(() => {
      if (unauthorizedConfirmationRequest === request) {
        unauthorizedConfirmationRequest = null;
      }
    }).catch(() => undefined);
    return request;
  }

  async function login(
    credentials: LoginCredentials,
  ): Promise<User | TwoFactorChallengeResponse> {
    const requestGeneration = beginAuthOperation();
    authStatus.value = "loading";
    authError.value = null;
    try {
      const result = await loginRequest(credentials);
      if (!ownsAuthOperation(requestGeneration)) return result;
      if (isTwoFactorChallenge(result)) {
        authStatus.value = "idle";
        return result;
      }
      return acceptAuthentication(result);
    } catch (caught) {
      if (!ownsAuthOperation(requestGeneration)) throw caught;
      throw failAuthentication(caught);
    }
  }

  async function completeTwoFactorLogin(challengeToken: string, code: string): Promise<User> {
    const requestGeneration = beginAuthOperation();
    authStatus.value = "loading";
    authError.value = null;
    try {
      const authenticatedUser = await completeLoginTotp({
        challenge_token: challengeToken,
        code,
      });
      if (!ownsAuthOperation(requestGeneration)) return authenticatedUser;
      return acceptAuthentication(authenticatedUser);
    } catch (caught) {
      if (!ownsAuthOperation(requestGeneration)) throw caught;
      throw failAuthentication(caught);
    }
  }

  async function register(details: RegistrationDetails): Promise<User> {
    const requestGeneration = beginAuthOperation();
    authStatus.value = "loading";
    authError.value = null;
    try {
      const authenticatedUser = await registerRequest(details);
      if (!ownsAuthOperation(requestGeneration)) return authenticatedUser;
      return acceptAuthentication(authenticatedUser);
    } catch (caught) {
      if (!ownsAuthOperation(requestGeneration)) throw caught;
      throw failAuthentication(caught);
    }
  }

  async function requestPasswordReset(identifier: string): Promise<string> {
    const requestGeneration = beginAuthOperation();
    authStatus.value = "loading";
    authError.value = null;
    try {
      const response = await passwordResetRequest(identifier);
      if (!ownsAuthOperation(requestGeneration)) return response.message;
      authStatus.value = "success";
      authError.value = null;
      return response.message;
    } catch (caught) {
      if (!ownsAuthOperation(requestGeneration)) throw caught;
      throw failAuthentication(caught);
    }
  }

  async function resetPassword(token: string, password: string): Promise<void> {
    const requestGeneration = beginAuthOperation();
    authStatus.value = "loading";
    authError.value = null;
    try {
      await passwordReset(token, password);
      if (!ownsAuthOperation(requestGeneration)) return;
      markUnauthenticated();
      authStatus.value = "success";
      authError.value = null;
    } catch (caught) {
      if (!ownsAuthOperation(requestGeneration)) throw caught;
      throw failAuthentication(caught);
    }
  }

  async function logout(): Promise<void> {
    const requestGeneration = beginAuthOperation();
    authStatus.value = "loading";
    authError.value = null;
    try {
      await logoutRequest();
      if (!ownsAuthOperation(requestGeneration)) return;
      markUnauthenticated();
      authStatus.value = "idle";
      authError.value = null;
    } catch (caught) {
      if (!ownsAuthOperation(requestGeneration)) throw caught;
      if (caught instanceof ApiError && caught.status === 401) {
        markUnauthenticated();
        authStatus.value = "idle";
        authError.value = null;
        return;
      }
      throw failAuthentication(caught);
    }
  }

  return {
    authStatus,
    authError,
    beginGoogleAccountLogin,
    beginMicrosoftAccountLogin,
    completeTwoFactorLogin,
    confirmUnauthorized,
    displayName,
    ensureSession,
    hasCapability,
    initial,
    isAuthenticated,
    loadLoginCapabilities,
    login,
    loginState,
    logout,
    markUnauthenticated,
    profile,
    profileStatus,
    refreshSession,
    register,
    requestPasswordReset,
    resetPassword,
    sessionError,
    user,
  };
});
